import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 3: VASTRA — Royal Luxury
 * Style: Royal, opulent, sophisticated Indian luxury fashion
 * Best for: Premium Indian fashion, Bridal couture, High-end ethnic wear
 * Deep wine/maroon theme with gold accents on warm ivory
 */
const Template3 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Collections
    const collections = [
        {
            name: 'Bridal Collection',
            description: 'Exquisite bridal ensembles crafted with centuries-old techniques and adorned with the finest embroidery.',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Festive Edit',
            description: 'Celebrate every occasion in opulent silks and rich textiles that honour our heritage.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
            name: 'Heritage Weaves',
            description: 'Timeless handloom masterpieces from the looms of India, woven with stories of generations past.',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Royal Banarasi Lehenga', price: '\u20B949,999', badge: 'NEW', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Silk Chanderi Saree', price: '\u20B918,500', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Gold Zardozi Sherwani', price: '\u20B932,000', badge: 'BESTSELLER', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Maroon Velvet Anarkali', price: '\u20B915,999', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Heritage Patola Dupatta', price: '\u20B912,499', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Ivory Bridal Lehenga', price: '\u20B945,000', badge: 'NEW', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Wine Silk Kurta Set', price: '\u20B95,999', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Festive Organza Saree', price: '\u20B921,500', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The craftsmanship is unparalleled. My bridal lehenga was a masterpiece of embroidery and silk that made me feel like royalty on my special day.',
            name: 'Priya Sharma',
            city: 'Jaipur',
            rating: 5
        },
        {
            text: 'VASTRA understands true Indian luxury. The sherwani I wore to my wedding was the finest garment I have ever owned. Every detail was perfect.',
            name: 'Rajesh Malhotra',
            city: 'Delhi',
            rating: 5
        },
        {
            text: 'From the first consultation to the final fitting, the experience was exceptional. The saree I received is a work of art that I will treasure forever.',
            name: 'Ananya Reddy',
            city: 'Hyderabad',
            rating: 5
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Lato', sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');
            `}</style>

            {/* ===== SECTION 1: SPLIT HERO ===== */}
            {/* Mobile: stacked layout */}
            <section className="lg:hidden relative w-full overflow-hidden" style={{ backgroundColor: '#FFFDF7' }}>
                <div className="w-full aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="Royal Indian Couture"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 py-10" style={{ backgroundColor: '#FFFDF7' }}>
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-3"
                        style={{ color: '#B8860B', fontFamily: "'Lato', sans-serif", fontWeight: 700 }}
                    >
                        Royal Indian Couture
                    </p>
                    <h1
                        className="text-3xl sm:text-4xl font-bold mb-4 leading-tight"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Where Heritage Meets Elegance
                    </h1>
                    <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                        Discover exquisite handcrafted Indian fashion, from opulent bridal lehengas to regal sherwanis.
                        Each piece is a testament to centuries of artisan tradition.
                    </p>
                    <button
                        onClick={() => navigate('/template3/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#722F37',
                            color: '#FFFFFF',
                            fontFamily: "'Lato', sans-serif",
                            fontWeight: 700,
                            border: '1px solid #722F37',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#5A1F2A';
                            e.currentTarget.style.borderColor = '#5A1F2A';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#722F37';
                            e.currentTarget.style.borderColor = '#722F37';
                        }}
                    >
                        Explore Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop: split layout — text left, image right */}
            <section className="hidden lg:block relative w-full min-h-screen overflow-hidden" style={{ backgroundColor: '#FFFDF7' }}>
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-screen flex items-center">
                    <div className="grid grid-cols-2 gap-16 items-center w-full">
                        {/* Left: Text */}
                        <div>
                            <div className="w-16 h-px mb-8" style={{ backgroundColor: '#B8860B' }} />
                            <p
                                className="text-sm tracking-[0.4em] uppercase mb-6"
                                style={{ color: '#B8860B', fontFamily: "'Lato', sans-serif", fontWeight: 700 }}
                            >
                                Royal Indian Couture
                            </p>
                            <h1
                                className="text-5xl xl:text-6xl font-bold mb-8 leading-[1.1]"
                                style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                            >
                                Where Heritage Meets Elegance
                            </h1>
                            <p
                                className="text-lg font-light mb-10 max-w-lg leading-relaxed"
                                style={{ color: '#6B7280', fontFamily: "'Lato', sans-serif" }}
                            >
                                Discover exquisite handcrafted Indian fashion, from opulent bridal lehengas to regal sherwanis.
                                Each piece is a testament to centuries of artisan tradition, reimagined for the modern connoisseur.
                            </p>
                            <div className="flex items-center gap-6">
                                <button
                                    onClick={() => navigate('/template3/shop')}
                                    className="group inline-flex items-center gap-3 px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-300"
                                    style={{
                                        backgroundColor: '#722F37',
                                        color: '#FFFFFF',
                                        border: '1px solid #722F37',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#5A1F2A';
                                        e.currentTarget.style.borderColor = '#5A1F2A';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#722F37';
                                        e.currentTarget.style.borderColor = '#722F37';
                                    }}
                                >
                                    Explore Collection <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button
                                    onClick={() => navigate('/template3/about')}
                                    className="inline-flex items-center gap-2 text-sm font-bold tracking-wide border-b pb-1 transition-all"
                                    style={{
                                        color: '#722F37',
                                        borderColor: 'rgba(114,47,55,0.3)',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#722F37'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(114,47,55,0.3)'; }}
                                >
                                    Our Story
                                </button>
                            </div>
                        </div>
                        {/* Right: Image */}
                        <div className="relative aspect-[3/4] overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                alt="Royal Indian Couture"
                                className="w-full h-full object-cover"
                            />
                            <div
                                className="absolute inset-0"
                                style={{ background: 'linear-gradient(to top, rgba(114,47,55,0.15) 0%, transparent 40%)' }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: COLLECTIONS ===== */}
            {/* Mobile: stacked */}
            <section className="lg:hidden" style={{ backgroundColor: '#FFF9F0' }}>
                <div className="px-4 py-12">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-2 text-center"
                        style={{ color: '#B8860B', fontWeight: 700 }}
                    >
                        Curated For You
                    </p>
                    <h2
                        className="text-2xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Our Collections
                    </h2>
                    <div className="flex flex-col gap-4">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(90,31,42,0.9) 0%, rgba(90,31,42,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3
                                        className="text-2xl font-bold mb-2"
                                        style={{ fontFamily: "'Playfair Display', serif", color: '#FFFFFF' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2"
                                        style={{ color: '#B8860B', fontWeight: 700 }}
                                    >
                                        Discover <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 3-column grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FFF9F0' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p
                        className="text-sm tracking-[0.3em] uppercase mb-3 text-center"
                        style={{ color: '#B8860B', fontWeight: 700 }}
                    >
                        Curated For You
                    </p>
                    <h2
                        className="text-4xl font-bold mb-16 text-center"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Our Collections
                    </h2>
                    <div className="grid grid-cols-3 gap-6">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template3/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(90,31,42,0.9) 0%, rgba(90,31,42,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <h3
                                        className="text-3xl font-bold mb-3"
                                        style={{ fontFamily: "'Playfair Display', serif", color: '#FFFFFF' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-4 leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300"
                                        style={{ color: '#B8860B', fontWeight: 700 }}
                                    >
                                        Discover <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCT GRID ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-12" style={{ backgroundColor: '#FFFDF7' }}>
                <div className="px-4">
                    <p className="text-xs tracking-[0.3em] uppercase mb-2 text-center" style={{ color: '#B8860B', fontWeight: 700 }}>
                        Handcrafted Luxury
                    </p>
                    <h2
                        className="text-2xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Featured Pieces
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template3/shop')}
                                className="cursor-pointer group"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D8',
                                    transition: 'border-color 0.3s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#722F37'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E0D8'; }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                                            style={{ backgroundColor: '#722F37', color: '#FFFFFF' }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: 'rgba(255,253,247,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#722F37' : '#6B7280',
                                                fill: wishlist[product.id] ? '#722F37' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <h3 className="text-xs font-normal line-clamp-1 mb-1" style={{ color: '#1A1A1A' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-xs font-bold" style={{ color: '#B8860B' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template3/shop')}
                        className="w-full mt-6 py-3.5 text-sm tracking-[0.15em] uppercase font-bold transition-all duration-300"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #722F37',
                            color: '#722F37',
                        }}
                    >
                        View All Collections
                    </button>
                </div>
            </section>

            {/* Desktop: 4-col grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FFFDF7' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-sm tracking-[0.3em] uppercase mb-3 text-center" style={{ color: '#B8860B', fontWeight: 700 }}>
                        Handcrafted Luxury
                    </p>
                    <h2
                        className="text-4xl font-bold mb-16 text-center"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Featured Pieces
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template3/shop')}
                                className="cursor-pointer group"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D8',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#722F37';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(114,47,55,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#E8E0D8';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-3 left-3 px-3 py-1 text-[10px] font-bold tracking-wider uppercase"
                                            style={{ backgroundColor: '#722F37', color: '#FFFFFF' }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: 'rgba(255,253,247,0.9)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#722F37' : '#6B7280',
                                                fill: wishlist[product.id] ? '#722F37' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-normal line-clamp-1 mb-2" style={{ color: '#1A1A1A' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-base font-bold" style={{ color: '#B8860B' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template3/shop')}
                            className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.2em] uppercase font-bold transition-all duration-300"
                            style={{
                                backgroundColor: 'transparent',
                                border: '1px solid #722F37',
                                color: '#722F37',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#722F37';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#722F37';
                            }}
                        >
                            View All Collections <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: BRAND HERITAGE ===== */}
            {/* Mobile */}
            <section className="lg:hidden" style={{ backgroundColor: '#FFF9F0' }}>
                <div className="w-full aspect-[4/3] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800"
                        alt="Our Heritage"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 py-10">
                    <div className="w-12 h-px mb-6" style={{ backgroundColor: '#B8860B' }} />
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-3"
                        style={{ color: '#B8860B', fontWeight: 700 }}
                    >
                        Our Heritage
                    </p>
                    <h2
                        className="text-3xl font-bold mb-5 leading-tight"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Centuries of Craft, One Garment at a Time
                    </h2>
                    <p className="text-sm leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                        Every VASTRA creation is born from the hands of master artisans who have inherited
                        their craft across generations. From the intricate zardozi embroidery of Lucknow
                        to the finest Banarasi silks, we honour the traditions that make Indian fashion
                        truly extraordinary.
                    </p>
                    <button
                        onClick={() => navigate('/template3/about')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 text-sm tracking-[0.2em] uppercase font-bold transition-all duration-300"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #722F37',
                            color: '#722F37',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#722F37';
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#722F37';
                        }}
                    >
                        Our Story <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop: image left, text right */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#FFF9F0' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 gap-16 items-center">
                        <div className="aspect-[4/5] overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="Our Heritage"
                                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>
                        <div>
                            <div className="w-16 h-px mb-8" style={{ backgroundColor: '#B8860B' }} />
                            <p
                                className="text-sm tracking-[0.3em] uppercase mb-4"
                                style={{ color: '#B8860B', fontWeight: 700 }}
                            >
                                Our Heritage
                            </p>
                            <h2
                                className="text-4xl xl:text-5xl font-bold mb-8 leading-tight"
                                style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                            >
                                Centuries of Craft, One Garment at a Time
                            </h2>
                            <p className="leading-relaxed mb-6" style={{ color: '#1A1A1A' }}>
                                Every VASTRA creation is born from the hands of master artisans who have inherited
                                their craft across generations. From the intricate zardozi embroidery of Lucknow
                                to the finest Banarasi silks, we honour the traditions that make Indian fashion
                                truly extraordinary.
                            </p>
                            <p className="text-sm leading-relaxed mb-10" style={{ color: '#6B7280' }}>
                                Our atelier brings together India's finest weavers, embroiderers, and designers
                                to create garments that are not merely worn, but experienced — each telling
                                a story of heritage, passion, and uncompromising quality.
                            </p>
                            <button
                                onClick={() => navigate('/template3/about')}
                                className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.2em] uppercase font-bold transition-all duration-300"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid #722F37',
                                    color: '#722F37',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#722F37';
                                    e.currentTarget.style.color = '#FFFFFF';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#722F37';
                                }}
                            >
                                Our Story <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#FFFDF7' }}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-xs lg:text-sm tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B', fontWeight: 700 }}>
                        Testimonials
                    </p>
                    <h2
                        className="text-2xl lg:text-4xl font-bold mb-12"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}
                    >
                        Words From Our Patrons
                    </h2>

                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        className="relative"
                    >
                        <div className="flex justify-center gap-1 mb-6">
                            {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                <Star key={i} className="w-5 h-5" style={{ color: '#B8860B', fill: '#B8860B' }} />
                            ))}
                        </div>
                        <p
                            className="text-base lg:text-xl italic leading-relaxed mb-8 max-w-2xl mx-auto"
                            style={{ fontFamily: "'Playfair Display', serif", color: '#1A1A1A' }}
                        >
                            "{testimonials[activeTestimonial].text}"
                        </p>
                        <p className="font-bold text-sm" style={{ color: '#722F37' }}>
                            {testimonials[activeTestimonial].name}
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                            {testimonials[activeTestimonial].city}
                        </p>

                        {/* Dots */}
                        <div className="flex justify-center gap-3 mt-8">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveTestimonial(idx)}
                                    className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                                    style={{
                                        backgroundColor: idx === activeTestimonial ? '#722F37' : '#E8E0D8',
                                        transform: idx === activeTestimonial ? 'scale(1.2)' : 'scale(1)',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: NEWSLETTER ===== */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#722F37' }}>
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="w-12 h-px mx-auto mb-6" style={{ backgroundColor: '#B8860B' }} />
                    <h2
                        className="text-2xl lg:text-4xl font-bold mb-4"
                        style={{ fontFamily: "'Playfair Display', serif", color: '#FFFFFF' }}
                    >
                        Join the Royal Circle
                    </h2>
                    <p className="text-sm lg:text-base mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Be the first to discover new collections, exclusive offers, and the stories behind our creations.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (email.trim()) setEmail(''); }}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            className="flex-1 px-5 py-3.5 text-sm outline-none"
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.25)',
                                color: '#FFFFFF',
                                fontFamily: "'Lato', sans-serif",
                            }}
                        />
                        <button
                            type="submit"
                            className="px-8 py-3.5 text-sm font-bold tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#B8860B',
                                color: '#FFFFFF',
                                border: '1px solid #B8860B',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#9A7209';
                                e.currentTarget.style.borderColor = '#9A7209';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#B8860B';
                                e.currentTarget.style.borderColor = '#B8860B';
                            }}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template3;
